import Button from "@/components/atoms/Button";
import { Paperclip, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import useAccess from "@/hooks/useAccess";
import Ticket from "@/types/Ticket";
import Reply from "@/types/Reply";
import ReplyForm from "@/components/organisms/Reply/Form";
import axios from "axios";
import { useEffect } from 'react';
import { toast } from "react-toastify";
import { SanitizedContent } from "@/hooks/useSanitize";

type Props = {
  replies: Reply[];
  ticket: Ticket;
  setSelectedTicketStatus: (status: string) => void;
};

const Chat = ({ replies, ticket, setSelectedTicketStatus }: Props) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<{success: boolean, message: string} | null>(null);
  const [visibleCount, setVisibleCount] = useState(5);
  const [editingReply, setEditingReply] = useState<Reply | null>(null);
  const { currentUser } = useAccess();
  const [sortedReplies, setSortedReplies] = useState<Reply[]>([
    ...replies
  ]);
  const [submitDefaultValues, setSubmitDefaultValues] = useState<any>(null);
  const [updateDefaultValues, setUpdateDefaultValues] = useState<any>(null);

  const handleSeeChat = () => {
    setVisibleCount((prevCount) => prevCount + 5);
  };

  const handleEdit = (reply: Reply) => {
    setEditingReply(reply);
   
    setUpdateDefaultValues(
      {
        cc: typeof reply.cc === "string" ? reply.cc.split(";") : reply.cc,
        message: reply.message,
        ticket_status_id: ticket.status.id,
        files: reply.attachments.map((file: any) => ({
          name: file.name,
          size: file.size,
          content_type: file.content_type,
          path: file.path,
          original_name: file.original_name,
          url: file.url,
        }))
      }
    );
  };

  const handleUpdate = async (data: any) => {
    setIsSubmitting(true);

    try {
      const payload = {
        reply: {
          message: data.message,
          cc: data.cc,
          ticket_id: ticket.id,
          user_id: currentUser?.id,
          ticket_status_id: data.ticket_status_id,
        },
        files: data.files.map((file: any) => ({
          filename: file.name,
          filesize: file.size,
          filetype: file.content_type,
          filepath: file.path,
          original_name: file.original_name,
          fileurl: file.url,
        })),
      };

      const response = await axios.put(`/replies/${editingReply?.id}`, payload, {
        headers: {
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      if (response.data.success) {
        setSelectedTicketStatus(data.ticket_status_id);
        setToastMessage({ success: true, message: response.data.message });
        setSortedReplies((prevReplies) =>
          prevReplies.map((reply) =>
            reply.id === editingReply?.id ? { ...reply, ...data } : reply
          )
        );
       
        setUpdateDefaultValues({ cc: [], message: "", ticket_status_id: data.ticket_status_id, files: [] });
        setSubmitDefaultValues({ cc: [], message: "", ticket_status_id: data.ticket_status_id, files: [] })
        setEditingReply(null);
      } else {
        setUpdateDefaultValues({ cc: data.cc, message: data.message, ticket_status_id: data.ticket_status_id, files: data.files });
        setToastMessage({ success: false, message: response.data.errors.join(', ') });
      }
    } catch (error) {
      setToastMessage({
        success: false,
        message: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (replyId: string | number) => {
    const confirmed = window.confirm('Are you sure you want to delete this reply?');

    if(confirmed) {
      try {
        const response = await axios.delete(`/replies/${replyId}`, {
          headers: {
            'X-CSRF-Token': getCsrfToken(),
          },
        });

        if (response.data.success) {
          setToastMessage({ success: true, message: response.data.message });
          setSortedReplies((prevReplies) => prevReplies.filter((reply) => reply.id !== replyId));
        } else {
          setToastMessage({ success: false, message: response.data.errors.join(', ') });
        }
      } catch (error) {
        if ((error as any).response) {
          setToastMessage({ success: false, message: (error as any).response.data.errors.join(', ') || 'You do not have permission to perform this action.' });
        } else {
          setToastMessage({ success: false, message: 'An unexpected error occurred. Please try again.' });
        }
      }
    }
      
  };

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute("content") || "" : "";
  };

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);

    const payload = {
      reply: {
        message: data.message,
        cc: data.cc,
        ticket_id: ticket.id,
        user_id: currentUser?.id,
        ticket_status_id: data.ticket_status_id,
      },
      files: data.files.map((file: any) => ({
        filename: file.name,
        filesize: file.size,
        filetype: file.content_type,
        filepath: file.path,
        original_name: file.original_name,
        fileurl: file.url,
      })),
    };

    try {
      const response = await axios.post(`/replies`, payload, {
        headers: {
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      if (response.data.success) {
        setSelectedTicketStatus(data.ticket_status_id);
        setToastMessage({ success: true, message: response.data.message });
        setSortedReplies((prevReplies) => [
          response.data.reply,
          ...prevReplies,
        ]);
        setSubmitDefaultValues({ cc: [], message: "", ticket_status_id: data.ticket_status_id, files: [] })
      } else {
        setToastMessage({ success: false, message: response.data.errors.join(', ') });
        setSubmitDefaultValues({ cc: data.cc, message: data.message, ticket_status_id: data.ticket_status_id, files: data.files })
      }
    } catch (error) {
      if ((error as any).response) {
        setToastMessage({ success: false, message: (error as any).response.data.errors.join(', ') || 'You do not have permission to perform this action.' });
      } else {
        setToastMessage({ success: false, message: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    if (toastMessage) {
      if (toastMessage.success) {
        toast.success(toastMessage.message);
      } else {
        toast.error(toastMessage.message);
      }
    }
  }, [toastMessage]);

  const formatDate = (input: string): string => {
    const date = new Date(input.replace(/(\d+)\/(\d+)\/(\d+)/, '$2/$1/$3'));
    
    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
  
    return date.toLocaleDateString('en-GB', options).replace(',', '');
  };

  return (
    <div className="w-full mx-auto p-4 shadow-lg rounded-lg">
      <div className="w-full pl-6 pt-4">
        <span className="text-xl font-bold">Chat</span>
      </div>
      <div className="w-full p-6">
        <div
          className="max-h-[900px] overflow-y-auto flex flex-col-reverse rounded-lg p-4"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#888 #e0e0e0",
          }}
        >
          {/* Replies */}
          {sortedReplies.slice(0, visibleCount).map((reply) => {
            const isCurrentUser = reply?.user?.id === currentUser?.id;

            return (
              <div
                key={reply.id}
                className={`flex ${isCurrentUser ? "justify-end" : "justify-start"} mb-4`}
              >
                <div
                  className={`w-5/6 p-4 ${isCurrentUser
                      ? "bg-blue-100 text-left"
                      : "text-left"
                    } border dark:border-border shadow-md rounded-lg`}
                >
                  <div
                    className={`flex ${isCurrentUser
                        ? "justify-end"
                        : "justify-start"
                      }`}
                  >
                    {isCurrentUser && (
                      <div className="flex space-x-2 mr-2 mt-2">
                        <Trash2
                          size={16}
                          className="cursor-pointer text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(reply.id)}
                        />

                        <Pencil
                          size={16}
                          className="cursor-pointer text-gray-500 hover:text-gray-800"
                          onClick={() => handleEdit(reply)}
                        />
                      </div>
                    )}
                    
                      {isCurrentUser ? (
                        <div className="flex space-x-4">
                          <div className="flex flex-col">
                            <p className="font-semibold">{reply?.user?.name}</p>
                            <span className="text-xs">{formatDate(reply?.created_at)}</span>
                            
                          </div>
                          <img
                            src={"avatar_url" in reply.user ? reply.user.avatar_url : "/default-avatar.png"}
                            alt="Profile"
                            className="w-12 h-12 rounded-full dark:bg-darkTextColor"
                          />
                        </div>
                      ): (
                        <div className="flex space-x-4">
                          <img
                            src={"avatar_url" in reply.user ? reply.user.avatar_url : "/default-avatar.png"}
                            alt="Profile"
                            className="w-12 h-12 rounded-full dark:bg-darkTextColor"
                          />
                          <div className="flex flex-col">
                            <p className="font-semibold">{reply?.user?.name}</p>
                            <span className="text-xs text-gray-500 dark:text-darkTextColor">{formatDate(reply?.created_at)}</span>
                          </div>
                        </div>
                      )}
                      

                  </div>
                  <div className="mt-4">
                      <SanitizedContent html={reply.message} />
                   
                  </div>
                  {reply.attachments && reply.attachments.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-semibold text-gray-800 dark:text-darkTextColor">Attachments:</p>
                      <ul className="mt-2">
                        {reply?.attachments?.map((attachment) => (
                          <div key={attachment.id} className="flex">
                            <Paperclip size={24} className="mr-2" />
                            <a
                              href={attachment.url || ''}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-bold  hover:underline block"
                            >
                              {attachment.original_name || attachment.name}
                            </a>
                          </div>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {visibleCount < sortedReplies.length && (
            <div className="flex justify-center mt-4 mb-5">
              <Button className="inline-flex items-center gap-2" onClick={handleSeeChat}>
                <span>Load More</span>
              </Button>
            </div>
          )}
        </div>

        <div className="flex justify-center mt-6">
          <div className="w-5/6 p-6 mb-4 shadow-md rounded-lg">
            <ReplyForm
              ticket={ticket}
              onSubmit={editingReply ? handleUpdate : handleSubmit}
              isSubmitting={isSubmitting}
              currentUser={currentUser}
              defaultValues={
                editingReply
                  ? updateDefaultValues
                  : submitDefaultValues
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
